import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { VideoPhone } from "@/components/public/video-phone"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { RelatedPages } from "@/components/public/related-pages"
import { relatedFor } from "@/data/related-pages"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const USE_CASES = [
  {
    title: "Airbnb and Booking.com listings",
    description:
      "Your listing photos compete with hundreds of others. A video walkthrough shared on social media drives direct bookings and builds recognition for your property. Guests who've seen a video arrive with higher expectations met, leading to better reviews.",
  },
  {
    title: "Seasonal marketing campaigns",
    description:
      "Summer availability, half-term deals, Christmas getaways. Create fresh video content for each season in 60 seconds. No need to wait for a photographer to visit the property again.",
  },
  {
    title: "Direct booking promotion",
    description:
      "Every booking through Airbnb costs you 3\u201315% in commission. Video marketing on Instagram and Facebook drives guests to your direct booking site, cutting out the middleman and protecting your margins.",
  },
  {
    title: "Portfolio marketing for agents",
    description:
      "Managing 20+ holiday lets? Create video content for every property in your portfolio. Show owners you're actively marketing their investment, not just listing it and waiting.",
  },
]

const FEATURES = [
  {
    title: "Works with any listing URL",
    description: "Paste a listing from Rightmove, Zoopla, OnTheMarket, Airbnb, or Booking.com. QuiteRight pulls the photos and details automatically.",
  },
  {
    title: "Scroll-stopping social video",
    description: "Videos formatted for Instagram Reels, TikTok, YouTube Shorts, and Facebook. Reach potential guests on the platforms where they discover travel inspiration.",
  },
  {
    title: "Professional voiceover",
    description: "AI-generated scripts and voiceover that highlight your property's best features, local attractions, and unique selling points.",
  },
  {
    title: "Your branding included",
    description: "Add your logo, property name, and booking contact details to every video. Build brand recognition across your holiday let portfolio.",
  },
  {
    title: "Seasonal refresh in seconds",
    description: "Update your video content as seasons change. Summer garden shots in June, cosy fireside in December. Same listing, fresh marketing, 60 seconds each.",
  },
  {
    title: "No filming required",
    description: "QuiteRight uses your existing listing photos. No need to arrange property access, hire a videographer, or wait for good weather.",
  },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "Does QuiteRight work with Airbnb listings?",
    a: "Yes. Paste any Airbnb, Booking.com, Rightmove, Zoopla, or OnTheMarket listing URL. QuiteRight imports the photos and property details automatically and creates a professional video ad.",
  },
  {
    q: "How does video marketing help with direct bookings?",
    a: "Video content on Instagram and Facebook reaches potential guests before they start searching on booking platforms. By including your direct booking URL or contact details in the video, you can drive bookings without paying platform commission fees of 3\u201315%.",
  },
  {
    q: "Can I create different videos for different seasons?",
    a: "Absolutely. Create a new video whenever you want, using different photos or highlighting different features. Summer garden views for the warm months, cosy interiors for winter. Each video takes about 60 seconds to create.",
  },
  {
    q: "How many holiday let videos can I create per month?",
    a: "The self-service plan at \u00A349/month lets you create videos on demand. The Managed plan at \u00A3299/month covers up to 50 videos, ideal for agents managing a portfolio of holiday properties.",
  },
  {
    q: "Is QuiteRight suitable for luxury holiday lets?",
    a: "Yes. QuiteRight's templates are designed to present properties professionally. For high-end holiday lets, the combination of quality imagery, AI voiceover, and branded presentation creates marketing content that matches the property's standard.",
  },
]

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function ForHolidayLets({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: meta.title,
            description: meta.description,
            url: meta.canonical,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "For Holiday Lets" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                For holiday lets
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
                More bookings, fewer platform fees
                <span className="text-red">.</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg text-slate">
                Turn your holiday property photos into scroll-stopping video ads for
                Instagram, TikTok, and Facebook. Drive direct bookings and reduce
                your dependence on Airbnb and Booking.com.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative flex justify-center">
              <VideoPhone />
              <div className="absolute -bottom-4 -left-4 rounded-lg bg-charcoal px-4 py-2.5 shadow-md md:-left-2">
                <p className="text-xs font-medium text-warm-grey">Drive direct bookings</p>
                <p className="font-display text-xl font-bold text-white">skip the fees</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Use cases
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              How holiday let owners use QuiteRight
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2">
            {USE_CASES.map((item, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <p className="font-display text-lg font-bold text-charcoal">{item.title}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.description}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Features
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Video marketing built for holiday lets
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <p className="font-display text-base font-bold text-charcoal">{feature.title}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate">{feature.description}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* The maths */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The maths
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-white md:text-4xl">
              One direct booking pays for a year
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            <ScrollReveal>
              <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                  Airbnb commission on &pound;1,000 booking
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-white">
                  &pound;150
                </p>
                <p className="mt-1 text-sm text-warm-grey">lost to the platform per booking</p>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="rounded-xl border border-red/30 bg-red/10 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-red">
                  QuiteRight annual cost
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-white">
                  &pound;588
                </p>
                <p className="mt-1 text-sm text-warm-grey">self-service plan, billed annually</p>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                  Direct bookings to break even
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-red">
                  4
                </p>
                <p className="mt-1 text-sm text-warm-grey">bookings saved from platform fees</p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <dl className="divide-y divide-stone">
                {FAQ.map((item, i) => (
                  <details key={i} className="group">
                    <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
                      <dt className="text-sm font-semibold leading-relaxed text-charcoal">
                        {item.q}
                      </dt>
                      <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} className="size-3">
                          <path d="M8 3v10M3 8h10" />
                        </svg>
                      </span>
                    </summary>
                    <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
                  </details>
                ))}
              </dl>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <RelatedPages links={relatedFor("for/holiday-lets")} />

      {/* CTA */}
      <section className="border-t border-stone bg-cream px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Fill your calendar, not Airbnb&rsquo;s pockets
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
              Professional video marketing for your holiday let in 60 seconds.
              Reach guests on social media, drive direct bookings, and keep
              more of every reservation.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
